import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Inbox, UserCheck, GitPullRequest } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth';
import { useLeadMutations } from '@/features/matters/hooks/useMatters';
import { useCase360My } from '../hooks/useCase360My';
import { dashboardQueryKeys } from '../api/dashboard.api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Case 360° (Stage 12) — "Awaiting my action": items addressed to *me*
 * across all matters. Most importantly Lead delegations pending my
 * Acknowledge (Stage 2) — without this block an incoming Lead can only
 * discover a request by opening that specific matter — plus PENDING change
 * requests I can decide (Stage 11). Hidden when empty.
 */
export function AwaitingMyActionCard() {
  const { t } = useTranslation('common');
  const { hasAccess } = useAuth();
  const queryClient = useQueryClient();
  const enabled = hasAccess('case360-statistic', 'get');

  const { data, isLoading } = useCase360My(enabled);
  const { acknowledgeMutation } = useLeadMutations();

  if (!enabled) return null;

  const delegations = data?.delegations ?? [];
  const changeRequests = data?.changeRequests ?? [];
  const total = delegations.length + changeRequests.length;

  if (!isLoading && total === 0) return null;

  const handleAcknowledge = async (matterId: number) => {
    await acknowledgeMutation.mutateAsync({ id: matterId });
    void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.case360My() });
  };

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <Inbox size={16} className="text-slate-400" />
          {t('dashboard.case360.awaitingTitle')}
          {total > 0 && (
            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
              {total}
            </span>
          )}
        </CardTitle>
        <CardDescription>{t('dashboard.case360.awaitingDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div key={i} className="h-10 rounded-md bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {delegations.map((d) => (
              <div
                key={`d-${d.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <UserCheck size={15} className="shrink-0 text-amber-600" />
                  <div className="min-w-0 text-sm">
                    <Link
                      to={`/app/matters/${d.matterId}`}
                      className="font-semibold text-[#0069D1] hover:underline"
                    >
                      {d.matterName ?? `#${d.matterId}`}
                    </Link>
                    <span className="text-slate-600">
                      {/* Who asked (Lead/Partner/MD); old rows fall back to the outgoing lead. */}
                      {' '}— {t('dashboard.case360.requestedBy', { name: d?.initiatedByName ?? d?.fromUserName ?? '—' })}
                      {d?.initiatedByName && d?.fromUserName && d.initiatedByName !== d.fromUserName
                        ? ` · ${t('dashboard.case360.delegationFrom', { name: d.fromUserName })}`
                        : ''}
                      {' · '}{formatDate(d.createdAt)}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="shrink-0 bg-amber-600 hover:bg-amber-700"
                  disabled={acknowledgeMutation.isPending}
                  onClick={() => handleAcknowledge(d.matterId)}
                >
                  {t('dashboard.case360.acknowledge')}
                </Button>
              </div>
            ))}

            {changeRequests.map((cr) => (
              <div
                key={`cr-${cr.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <GitPullRequest size={15} className="shrink-0 text-slate-500" />
                  <div className="min-w-0 text-sm">
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[0.7rem] font-semibold text-slate-700 mr-1.5">
                      {t(`cr.type.${cr.type}`)}
                    </span>
                    <Link
                      to={`/app/matters/${cr.matter?.id}?tab=plan`}
                      className="font-semibold text-[#0069D1] hover:underline"
                    >
                      {cr.matter?.name ?? `#${cr.matter?.id}`}
                    </Link>
                    <span className="text-slate-600">
                      {' '}— {cr.author?.name ?? '—'} · {formatDate(cr.createdAt)}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/app/matters/${cr.matter?.id}?tab=plan`}
                  className="shrink-0 text-sm font-medium text-[#0069D1] hover:underline"
                >
                  {t('dashboard.case360.review')}
                </Link>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
